import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TodoItemResponse, TodoSectionResponse } from '@/lib/api';

interface EditItemDialogProps {
  open: boolean;
  onClose: () => void;
  item: TodoItemResponse | null;
  sections: TodoSectionResponse[];
  onSave: (
    itemId: string,
    input: { title?: string; description?: string; sectionId?: string | null },
  ) => void;
}

export function EditItemDialog({ open, onClose, item, sections, onSave }: EditItemDialogProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [sectionId, setSectionId] = useState<string>('none');

  useEffect(() => {
    if (open && item) {
      setTitle(item.title);
      setDescription(item.description ?? '');
      setSectionId(item.sectionId ?? 'none');
    }
  }, [open, item]);

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      onClose();
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!item || !title.trim()) return;

    const newSection = sectionId === 'none' ? null : sectionId;
    const oldSection = item.sectionId ?? null;

    onSave(item.itemId, {
      title: title.trim(),
      description: description.trim() || undefined,
      ...(newSection !== oldSection ? { sectionId: newSection } : {}),
    });
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Item</DialogTitle>
          <DialogDescription>Update the item details.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-item-title">Title</Label>
            <Input
              id="edit-item-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-item-description">Description (optional)</Label>
            <Textarea
              id="edit-item-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Section</Label>
            <Select value={sectionId} onValueChange={setSectionId}>
              <SelectTrigger>
                <SelectValue placeholder="Unsorted" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unsorted</SelectItem>
                {sections.map((s) => (
                  <SelectItem key={s.sectionId} value={s.sectionId}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim()}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
